import { useQuery } from '@tanstack/react-query';
import { API_BASE } from './api-base';
import { apiFetch } from './api-client';

// How often to re-read the truthful heartbeat. The KV self-expires at ~90s and the worker beats every
// turn, so a ~20s poll keeps the pill dot / plan marker fresh without hammering.
const POLL_MS = 20_000;

/**
 * Whether a worker loop is executing on this task RIGHT NOW — the truthful signal behind the green
 * "working" dot (pill) and the plan's live marker. Reads the liveness heartbeat the worker writes to a
 * self-decaying KV, via `/api/task/:id/liveness` → the native-data `task` action (the SAME signal native
 * reads directly). A dead/parked loop stops beating and the KV expires, so this reads false — "in
 * progress" alone never lights it. Best-effort: any failure reads not-live.
 *
 * Signature is intentionally the plain `(taskId) => boolean` both the pill's lead and the task-detail
 * marker already call — this replaced an earlier recent-beat proxy with no call-site change.
 */
export function useTaskLiveness(taskId: string): boolean {
  const { data } = useQuery({
    queryKey: ['task-liveness', taskId],
    enabled: !!taskId,
    refetchInterval: POLL_MS,
    refetchIntervalInBackground: false,
    queryFn: async () => {
      const res = await apiFetch(`${API_BASE}/api/task/${encodeURIComponent(taskId)}/liveness`, { credentials: 'include' });
      if (!res.ok) return { isLive: false };
      return (await res.json()) as { isLive?: boolean };
    },
  });
  return data?.isLive === true;
}
